import {
  Skeleton,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Icon,
  Flex,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';
import {
  FundingRequest as FundingRequestTypes,
  Util,
} from '@finley-technologies/finley-types';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  FundingRequestComponentTypeEnumGQLType,
  UpdateFundingRequestComponentChangesetGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';
import {
  FundingRequestComponentContainer,
  FundingRequestComponentDefaultTargetFundingDates,
} from 'types/fundingRequests';

import { colors } from 'packages/common-frontend/colors';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { fundingRequestKeyToLabel } from 'packages/common-frontend/constants';
import {
  bankAccountToDisplayString,
  dollarNumberWithCommas,
} from 'packages/common-frontend/utils';
import { useGetOrganizationsByIdsQuery } from 'packages/organization/requests/organization.gql.generated';
import { FiCalendar } from 'react-icons/fi';

/**
 * typeguard method, in case a user is constructing `components` data
 */
export const isFundingRequestComponentsTableParams = (
  val: any,
): val is FundingRequestComponentContainer => {
  return (
    val &&
    'curePaydown' in val &&
    'collectionsPaydown' in val &&
    'collectionsRecycle' in val &&
    'drawRequest' in val
  );
};

export type TargetFundingDateEditFunction = (
  id: string,
  type: FundingRequestComponentTypeEnumGQLType,
  fullDateStr: Util.FullDateStr,
) => any;

/**
 * Renders a funding request component table row
 */
const FundingRequestComponentTableRow = ({
  bankAccountMap,
  fundingRequestComponent,
  defaultTargetFundingDate,
  onTargetFundingDateEdit,
  componentFrequencyMessage,
  addedContributionMessage,
}: {
  bankAccountMap: Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType & {
      organizationName?: string;
    }
  >;
  fundingRequestComponent: UpdateFundingRequestComponentChangesetGQLType;
  defaultTargetFundingDate: undefined | Util.FullDateStr;
  onTargetFundingDateEdit: undefined | TargetFundingDateEditFunction;
  componentFrequencyMessage: undefined | string;
  addedContributionMessage: undefined | string;
}) => {
  // Target funding date
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = new Date();
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
  const selectedDate = fundingRequestComponent.targetFundingDate
    ? commonDate.fullDateStrToDate(fundingRequestComponent.targetFundingDate)
    : undefined;

  return (
    <Tr height="160px">
      <Td>
        {fundingRequestKeyToLabel[fundingRequestComponent.type]}
        {componentFrequencyMessage && (
          <Text
            position="absolute"
            width="180px"
            color="red.400"
            fontSize="12px"
            lineHeight="base"
          >
            {componentFrequencyMessage}
          </Text>
        )}
      </Td>
      <Td>
        {dollarNumberWithCommas(fundingRequestComponent.amount, 2)}
        {addedContributionMessage && (
          <Text
            position="absolute"
            width="180px"
            fontSize="12px"
            lineHeight="base"
            whiteSpace="pre-line"
          >
            {addedContributionMessage}
          </Text>
        )}
      </Td>
      <Td>
        {bankAccountToDisplayString(
          bankAccountMap.get(fundingRequestComponent.sourceBankAccountId),
        )}
      </Td>
      <Td>
        {bankAccountToDisplayString(
          bankAccountMap.get(fundingRequestComponent.destinationBankAccountId),
        )}
      </Td>
      <Td>
        {!onTargetFundingDateEdit &&
          fundingRequestComponent.targetFundingDate &&
          commonDate.fullDateStrToHumanDateString(
            fundingRequestComponent.targetFundingDate,
          )}

        {onTargetFundingDateEdit && (
          <Flex>
            <SimpleDatePicker
              width="170px"
              tz={clientTimeZone}
              dateFormat="MMMM dd, yyyy"
              minDate={today}
              maxDate={maxDate}
              selectedDate={selectedDate}
              showTimeSelect={false}
              setSelectedDate={(date: Date) => {
                const fullDateStr = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                onTargetFundingDateEdit(
                  fundingRequestComponent.id,
                  fundingRequestComponent.type,
                  fullDateStr,
                );
              }}
            />
            <Icon
              as={FiCalendar}
              position="relative"
              right="20px"
              h="inherit"
              pointerEvents="none"
            />
          </Flex>
        )}
        {/* If editable and a default target funding date is present, display a warning only for draw request components when target funding date is adjusted */}
        {onTargetFundingDateEdit &&
          defaultTargetFundingDate &&
          fundingRequestComponent.targetFundingDate !==
            defaultTargetFundingDate && (
            <Text
              position="absolute"
              width="180px"
              color="red.400"
              fontSize="12px"
              lineHeight="base"
            >
              Warning: This change requires approval from your lender(s).
              Default:{' '}
              {commonDate.fullDateStrToHumanDateString(
                defaultTargetFundingDate,
              )}
            </Text>
          )}
      </Td>
    </Tr>
  );
};

/**
 * React component to display the funding request summary
 */
export const FundingRequestComponentsTable = ({
  bankAccountMap,
  fundingRequestComponents,
  isCollectionsRecycleEnabled,
  isVoluntaryPaydownAllowed,
  maxFundingRequestAmount,
  defaultTargetFundingDates,
  onTargetFundingDateEdit,
  drawRequestFrequencyMessage,
  prefundedDrawContributionMessage,
}: {
  bankAccountMap: Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType & {
      organizationName?: string;
    }
  >;
  fundingRequestComponents: FundingRequestComponentContainer;
  isCollectionsRecycleEnabled: boolean;
  isVoluntaryPaydownAllowed: boolean;
  maxFundingRequestAmount: number;
  defaultTargetFundingDates?: FundingRequestComponentDefaultTargetFundingDates;
  onTargetFundingDateEdit?: undefined | TargetFundingDateEditFunction;
  drawRequestFrequencyMessage?: string | undefined;
  prefundedDrawContributionMessage?: string | undefined;
}) => {
  const {
    data: organizationsData,
    loading: organizationsLoading,
  } = useGetOrganizationsByIdsQuery({
    variables: {
      organizationIds: Array.from(
        bankAccountMap.values(),
        (bankAccount) => bankAccount.organizationId,
      ),
    },
  });
  if (organizationsLoading || !organizationsData) {
    return (
      <Skeleton data-testid="loading-funding-request-table" height="200px" />
    );
  }

  const addOrganizationNameToBankAccount = (
    bankAccount: BankAccountWithInstitutionAndDesignationGQLType & {
      organizationName?: string;
    },
  ) => {
    const organization = _.find(organizationsData.organizationsByIds, {
      id: bankAccount.organizationId,
    });
    const organizationName = organization ? organization.name : '';
    return {
      ...bankAccount,
      organizationName,
    };
  };
  bankAccountMap.forEach((bankAccount, key) => {
    bankAccountMap.set(key, addOrganizationNameToBankAccount(bankAccount));
  });

  const componentsArray = _.toPairs(fundingRequestComponents);
  const renderedComponents = _.reduce(
    componentsArray,
    (acc, [componentKey, componentArray]) => {
      const _renderComponents = _.filter(componentArray, (currComponent) => {
        const { type } = currComponent;

        if (currComponent.amount === 0) return false;

        switch (type) {
          case 'curePaydown':
            if (maxFundingRequestAmount < 0) return true;
            return false;
          case 'collectionsPaydown':
            if (
              isVoluntaryPaydownAllowed === true ||
              (isVoluntaryPaydownAllowed === false &&
                maxFundingRequestAmount <= 0)
            )
              return true;
            return false;
          case 'collectionsRecycle':
            return isCollectionsRecycleEnabled === true;
          default:
            return true;
        }
      });
      const renderedComponentPairs: [
        string,
        UpdateFundingRequestComponentChangesetGQLType,
      ][] = _.map(_renderComponents, (component) => {
        return [componentKey, component];
      });

      return [...acc, ...renderedComponentPairs];
    },
    [] as [string, UpdateFundingRequestComponentChangesetGQLType][],
  );

  const headerTexts = [
    'Transaction',
    'Amount',
    'Source',
    'Destination',
    'Funding Date',
  ];
  const headerCells = _.concat(
    _.map(headerTexts, (headerText) => {
      return <Th key={`key-${Math.random()}`}>{headerText}</Th>;
    }),
  );

  const getComponentFrequencyMessage = (
    componentType: string,
  ): string | undefined => {
    switch (componentType) {
      case FundingRequestTypes.FundingRequestComponentType.DrawRequest:
        return drawRequestFrequencyMessage;
      default:
        return undefined;
    }
  };

  const getComponentAddedContributionMessage = (
    componentType: string,
  ): string | undefined => {
    switch (componentType) {
      case FundingRequestTypes.FundingRequestComponentType.DrawRequest:
        return prefundedDrawContributionMessage;
      default:
        return undefined;
    }
  };

  return (
    <Table size="lg" variant="simple" mb="40px" borderWidth="1px">
      <Thead background={colors.tableHeader} color="#979797">
        <Tr>{headerCells}</Tr>
      </Thead>
      <Tbody>
        {_.map(renderedComponents, ([, fundingRequestComponent]) => {
          return (
            <FundingRequestComponentTableRow
              key={`key-${Math.random()}`}
              bankAccountMap={bankAccountMap}
              fundingRequestComponent={fundingRequestComponent}
              defaultTargetFundingDate={
                defaultTargetFundingDates &&
                defaultTargetFundingDates[fundingRequestComponent.id]
              }
              onTargetFundingDateEdit={onTargetFundingDateEdit}
              componentFrequencyMessage={getComponentFrequencyMessage(
                fundingRequestComponent.type,
              )}
              addedContributionMessage={getComponentAddedContributionMessage(
                fundingRequestComponent.type,
              )}
            />
          );
        })}
      </Tbody>
    </Table>
  );
};

FundingRequestComponentsTable.defaultProps = {
  defaultTargetFundingDates: {},
  drawRequestFrequencyMessage: undefined,
  onTargetFundingDateEdit: undefined,
  prefundedDrawContributionMessage: undefined,
};
